// TIP-609 — the briefs table.
//
// Columns in the designed order and proportion: Target company, Country,
// Ordered, Completed, Status. Search matches company and country only. The
// status filter is a dropdown, not a row of pills, each option carrying a
// count. Ten rows a page with numbered pagination.
//
// Two deliberate departures from the design:
//   - the empty Completed cell renders as an EMPTY CELL. The design puts a
//     literal em dash in the data, on four of the fourteen rows, so it ships
//     (inconsistency 21) and TREVEX copy does not use em dashes.
//   - the paging label reads "Showing 1 to 10 of 14" — the word "to", not an
//     en dash (inconsistency 22).
import React, { useEffect, useMemo, useRef, useState } from "react";
import "./counterpartyBriefs.css";
import StatusChip, { DownloadButton } from "./StatusChip";
import CbPanel from "./CbPanel";
import CbButton from "./CbButton";
import BriefsTableShell, { EmptyRow } from "./BriefsTableShell";
import { ReactComponent as EmptyIllustration } from "../../../../assets/icons/PurchaseCompany.svg";
import { BRIEF_STATUSES, FILTER_LABELS, ALL_STATUSES, TABLE_COPY } from "./briefsFixtures";

const PAGE_SIZE = 10;

const StatusFilter = ({ value, counts, total, statuses, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const options = [
    { key: ALL_STATUSES, label: TABLE_COPY.filterAll, count: total },
    ...statuses.map((s) => ({ key: s, label: FILTER_LABELS[s], count: counts[s] || 0 })),
  ];
  const currentLabel = value === ALL_STATUSES ? TABLE_COPY.filterAll : FILTER_LABELS[value];

  return (
    <div className="cb-filter" ref={ref}>
      <button
        type="button"
        className="cb-control cb-filter-button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Filter by status"
      >
        <span>{currentLabel}</span>
        <svg className="cb-filter-caret" width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
          <path d="M2.5 4.5 6 8l3.5-3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <div role="listbox" className="cb-filter-pop">
          {options.map((o) => (
            <button
              key={o.key}
              type="button"
              role="option"
              aria-selected={o.key === value}
              className={`cb-filter-option${o.key === value ? " is-selected" : ""}`}
              onClick={() => { onChange(o.key); setOpen(false); }}
            >
              <span>{o.label}</span>
              <span className="cb-filter-count">{o.count}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const BriefsTable = ({
  briefs,
  statuses,
  statusCounts,
  onStatusClick,
  onOrder,
  onDownload,
  // TIP-610 — the reference whose download is in flight, and why the last one
  // failed. Both belong to the page's mutation, not to this table.
  downloadingRef = null,
  downloadError = null,
  onDismissDownloadError,
  // TIP-610 — server-driven paging, search and status filtering.
  //
  // Given, every one of the three is a request: the rows in `briefs` are one
  // page of an already-filtered set, and the counts and totals come from the
  // backend. Omitted (the ?cbState= review path, which has no backend), the
  // table keeps its own state and filters the rows it was handed.
  server = null,
  // TIP-610 — the list is its own read, so it has its own two states. Neither
  // is the empty state: "nothing ordered yet" and "we could not tell you what
  // you ordered" are different sentences.
  isLoading = false,
  error = null,
  onRetry,
}) => {
  const [ownQuery, setOwnQuery] = useState("");
  const [ownStatus, setOwnStatus] = useState(ALL_STATUSES);
  const [ownPage, setOwnPage] = useState(0);

  // The three controls read from whichever side owns them.
  const query = server ? server.query : ownQuery;
  const status = server ? server.status : ownStatus;

  const clientFiltered = useMemo(() => {
    if (server) return briefs;
    const q = ownQuery.trim().toLowerCase();
    return briefs.filter((b) => {
      const matchesQuery = !q || b.company.toLowerCase().includes(q) || b.country.toLowerCase().includes(q);
      return matchesQuery && (ownStatus === ALL_STATUSES || b.status === ownStatus);
    });
  }, [server, briefs, ownQuery, ownStatus]);

  // Server-driven: the rows ARE the page, and the counts come with them.
  // Client-driven: slice the filtered rows.
  const pageCount = server
    ? Math.max(1, server.pageCount)
    : Math.max(1, Math.ceil(clientFiltered.length / PAGE_SIZE));
  // Both are held zero-based internally; the server's page is one-based.
  const current = server ? Math.max(0, server.page - 1) : Math.min(ownPage, pageCount - 1);
  const rows = server ? briefs : clientFiltered.slice(current * PAGE_SIZE, current * PAGE_SIZE + PAGE_SIZE);
  // The count the paging label reports: the filtered total either way.
  const shown = server ? server.total : clientFiltered.length;
  const from = shown === 0 ? 0 : current * PAGE_SIZE + 1;
  const to = current * PAGE_SIZE + rows.length;

  const setQuery = (value) => (server ? server.onQuery(value) : setOwnQuery(value));
  const setStatus = (value) => (server ? server.onStatusFilter(value) : setOwnStatus(value));
  // Zero-based in, one-based out.
  const goToPage = (index) => (server ? server.onPage(index + 1) : setOwnPage(index));
  const resetPage = () => (server ? undefined : setOwnPage(0));

  // "You have never ordered one" versus "nothing matches this view" — two
  // different sentences, and only the FULL count can tell them apart. With a
  // filter applied, an empty page means no matches, not an empty account.
  const totalAll = server ? server.totalAll : briefs.length;
  const isEmpty = !isLoading && !error && totalAll === 0;
  const noMatches = totalAll > 0 && rows.length === 0;

  return (
    <CbPanel variant="table" flush>
      <div className="cb-table-head">
        <h3 className="cb-table-title">
          {TABLE_COPY.heading}
          {/* Suppressed until the list is in hand: a "0" beside the heading
              while the request is out reads as "you have never ordered one".

              `totalAll`, not the rows on screen. Once the table pages on the
              server, `briefs` is ONE page — a company with 25 briefs would
              have had this badge say 10. It reads the same as before for any
              account small enough to fit on a page, which is why it looked
              right. */}
          {!isLoading && !error && <span className="cb-table-count">{totalAll}</span>}
        </h3>

        <div className="cb-table-controls">
          <div className="cb-control cb-search">
            <svg className="cb-search-icon" width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
              <circle cx="6" cy="6" r="4.3" stroke="currentColor" strokeWidth="1.4" />
              <path d="M9.3 9.3 12.2 12.2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
            </svg>
            <input
              className="cb-search-input"
              value={query}
              onChange={(e) => { setQuery(e.target.value); resetPage(); }}
              placeholder={TABLE_COPY.searchPlaceholder}
              aria-label={TABLE_COPY.searchPlaceholder}
            />
          </div>

          <StatusFilter
            value={status}
            counts={statusCounts}
            total={totalAll}
            statuses={statuses}
            onChange={(v) => { setStatus(v); resetPage(); }}
          />
        </div>
      </div>

      {isLoading && (
        <p role="status" data-testid="cb-briefs-loading" className="cb-table-state">
          {TABLE_COPY.loading}
        </p>
      )}

      {!isLoading && error && (
        <div role="alert" data-testid="cb-briefs-error" className="cb-table-state">
          <p className="cb-table-state-heading">{TABLE_COPY.errorHeading}</p>
          <p>{TABLE_COPY.errorBody}</p>
          {onRetry && (
            <CbButton variant="ghost" onClick={onRetry}>{TABLE_COPY.errorRetry}</CbButton>
          )}
        </div>
      )}

      {/* TIP-610 — a download that did not happen. Above the rows, because the
          row's own button has already gone back to saying Download and there
          would otherwise be nothing to show the customer that a click they
          made did nothing. Dismissible: it is about one click, not the state
          of the list. */}
      {downloadError && (
        <div role="alert" data-testid="cb-download-error" className="cb-download-error">
          <p className="cb-download-error-text">{downloadError.message}</p>
          {onDismissDownloadError && (
            <button
              type="button"
              className="cb-download-error-close"
              onClick={onDismissDownloadError}
              aria-label={TABLE_COPY.downloadErrorDismiss}
            >
              &#215;
            </button>
          )}
        </div>
      )}

      {!isLoading && !error && !isEmpty && (
        <BriefsTableShell columns={TABLE_COPY.columns}>
          {noMatches && (
            <EmptyRow
              colSpan={TABLE_COPY.columns.length}
              heading={TABLE_COPY.noMatchHeading}
              body={TABLE_COPY.noMatchBody}
            />
          )}
          {rows.map((b) => (
            <tr key={b.id}>
              <td className="cb-td">{b.company}</td>
              <td className="cb-td">{b.country}</td>
              <td className="cb-td cb-td--date">{b.ordered}</td>
              {/* Empty cell, not an em dash. */}
              <td className="cb-td cb-td--date">{b.completed || ""}</td>
              <td className="cb-td">
                <div className="cb-status-cell">
                  <StatusChip status={b.status} onClick={() => onStatusClick(b)} />
                  {/* Readiness is the whole gate. It used to also require
                      `downloadUrl !== null`, which no list row has ever
                      carried — the endpoint returns the file rather than a
                      link to it, so there is no URL to wait for and the
                      button was unreachable. The server re-checks the status,
                      so a stale Ready row cannot leak an unfinished PDF. */}
                  {b.status === BRIEF_STATUSES.READY && (
                    <DownloadButton
                      onClick={() => onDownload(b)}
                      busy={downloadingRef === b.reference}
                    />
                  )}
                </div>
              </td>
            </tr>
          ))}
        </BriefsTableShell>
      )}

      {isEmpty && (
        <div className="cb-empty-state">
          <EmptyIllustration aria-hidden="true" className="cb-empty-art" />
          <span className="cb-empty-text">{TABLE_COPY.emptyBody}</span>
          <CbButton variant="ghost" onClick={onOrder}>{TABLE_COPY.emptyAction}</CbButton>
        </div>
      )}

      {!isLoading && !error && pageCount > 1 && (
        <div className="cb-paging">
          <span className="cb-paging-label">{TABLE_COPY.paging(from, to, shown)}</span>
          <div className="cb-paging-controls">
            <button type="button" className="cb-pager" onClick={() => goToPage(Math.max(0, current - 1))} disabled={current === 0}>
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                <path d="M7.5 2.5 4 6l3.5 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              {TABLE_COPY.previous}
            </button>

            {Array.from({ length: pageCount }, (_, i) => i).map((i) =>
              i === current ? (
                <span key={i} className="cb-page-num--current" aria-current="page">{i + 1}</span>
              ) : (
                <button key={i} type="button" className="cb-page-num" onClick={() => goToPage(i)} aria-label={`Page ${i + 1}`}>
                  {i + 1}
                </button>
              )
            )}

            <button type="button" className="cb-pager" onClick={() => goToPage(Math.min(pageCount - 1, current + 1))} disabled={current >= pageCount - 1}>
              {TABLE_COPY.next}
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                <path d="M4.5 2.5 8 6l-3.5 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </CbPanel>
  );
};

export default BriefsTable;
